import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useState, useEffect } from "react";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEscape);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    if (feedback.length > 400) {
      setErrorMessage("A visszajelzés nem lehet hosszabb 400 karakternél.");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ feedback }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Hiba történt a küldés során.");
      }

      setStatus("success");
      setFeedback("");
    } catch (err: any) {
      setErrorMessage(
        err.message || "Hiba történt a visszajelzés elküldésekor. Kérjük, próbáld újra!",
      );
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
          onClick={onClose}
        >
          {/* Backdrop wrapper to handle click-away correctly */}
          <div className="absolute inset-0" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="w-full max-w-[400px] rounded-2xl border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.85)] bg-[#1a1a1a]/95 flex flex-col overflow-hidden relative z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ambient Background Glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#22D3BA]/10 to-[#F7C4FF]/10 blur-2xl pointer-events-none" />

            {/* Header: Support-band's masking-tape-gradient */}
            <div className="masking-tape-gradient px-6 py-4 border-b border-white/10 flex justify-between items-center relative z-10 text-[#0F0F0F]">
              <div className="flex flex-col relative z-20">
                <h2 className="font-['Unbounded'] text-sm font-black uppercase tracking-wider">
                  Visszajelzés
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-[#0F0F0F]/70 hover:text-[#0F0F0F] font-bold p-1 relative z-20 transition-colors"
                aria-label="Bezárás"
              >
                ✕
              </button>
            </div>

            {status === "success" ? (
              <>
                <div className="bg-black/40 px-6 py-8 relative z-10 flex-1 flex flex-col items-center justify-center text-center">
                  <span className="text-4xl mb-4">🎉</span>
                  <p className="text-sm font-semibold text-white mb-2">
                    Köszönjük a visszajelzést!
                  </p>
                  <p className="text-xs text-muted leading-relaxed">
                    Véleményed segít a Tilos Maraton app és a fesztivál fejlesztésében.
                  </p>
                </div>
                <div className="bg-black/20 px-6 py-4 border-t border-white/5 relative z-10">
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full bg-main text-[#222222] font-['Unbounded'] font-bold text-xs py-2.5 rounded-xl hover:brightness-110 active:scale-[0.98] transition-all uppercase tracking-wider"
                  >
                    Bezárás
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 relative z-10">
                <div className="bg-black/40 px-6 py-6 relative z-10 flex-1 flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="feedback-text" className="text-xs font-semibold text-white/90">
                      Üzenet (maximum 400 karakter):
                    </label>
                    <textarea
                      id="feedback-text"
                      required
                      value={feedback}
                      onChange={(e) => {
                        if (status === "error") setStatus("idle");
                        setFeedback(e.target.value);
                      }}
                      placeholder="Írd le a véleményed, észrevételed..."
                      maxLength={400}
                      rows={5}
                      disabled={status === "submitting"}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-xs text-white placeholder-white/20 focus:outline-none focus:border-main/50 transition-all resize-none leading-relaxed"
                    />
                    <div className="flex justify-between items-center text-[10px] mt-0.5">
                      <span
                        className={
                          feedback.length >= 400 ? "text-orange font-semibold" : "text-muted"
                        }
                      >
                        {feedback.length} / 400 karakter
                      </span>
                      {feedback.length >= 350 && (
                        <span className="text-orange animate-pulse">Közel a limithez!</span>
                      )}
                    </div>
                  </div>

                  {status === "error" && (
                    <p className="text-xs text-orange bg-orange/10 border border-orange/25 rounded-lg p-2.5 leading-relaxed">
                      ⚠️ {errorMessage}
                    </p>
                  )}
                </div>

                <div className="bg-black/20 px-6 py-4 border-t border-white/5 relative z-10 flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={status === "submitting"}
                    className="flex-1 border border-white/10 text-white font-['Unbounded'] font-bold text-[10px] py-2.5 rounded-xl hover:bg-white/5 active:scale-[0.98] transition-all uppercase tracking-wider disabled:opacity-50"
                  >
                    Mégsem
                  </button>
                  <button
                    type="submit"
                    disabled={status === "submitting" || !feedback.trim()}
                    className="flex-1 bg-main text-[#222222] font-['Unbounded'] font-bold text-[10px] py-2.5 rounded-xl hover:brightness-110 active:scale-[0.98] transition-all uppercase tracking-wider disabled:opacity-50"
                  >
                    {status === "submitting" ? "Küldés..." : "Küldés"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
